/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { fetchAsset, getTheme } from '@/api/dataroom';
import type { ThemeResponse } from '@/api/types';
import { applyDesignVars, loadHubFonts } from './hubAssets';

/**
 * Per-hub white-label theme state (OPSFL-CLIENT-PLATFORM P1). The theme is SESSION-SCOPED on the
 * server (the hub comes from the bearer), so `load()` is triggered by the session watcher once a
 * session is active — never before, never from a URL parameter. Failure is NON-FATAL by
 * contract: the room renders its built-in identity byte-identically when un-themed, and a theme
 * fetch error is indistinguishable from "no theme" (I5 applies server-side; client-side a
 * broken theme must never block an applicant from their tasks).
 *
 * DOM side effects owned here (the same layer as applyThemeVars): the hub favicon (injected —
 * index.html deliberately ships none), document.title (product name), the sanitised custom-CSS
 * <style> tag, the layout-variant attribute on <html>, and OL-23's hub fonts and legal floor.
 */
export interface HubThemeState {
  theme: ThemeResponse | null;
  load: () => void;
}

const ThemeContext = createContext<HubThemeState>({ theme: null, load: () => {} });

export function useHubTheme(): HubThemeState {
  return useContext(ThemeContext);
}

const STYLE_ID = 'ol-hub-css';
const FAVICON_ID = 'ol-hub-favicon';

function applyDomEffects(theme: ThemeResponse | null): void {
  const root = document.documentElement;
  const themed = theme?.themed === true;

  // Layout variant — global.css keys off [data-variant]; absence = CLASSIC = today's layout.
  const variant = themed ? (theme?.layoutVariant ?? 'CLASSIC') : 'CLASSIC';
  if (variant === 'CLASSIC') {
    delete root.dataset.variant;
  } else {
    root.dataset.variant = variant;
  }

  // Custom CSS — server-sanitised (G23); injected verbatim, replaced wholesale, never merged.
  const existingStyle = document.getElementById(STYLE_ID);
  if (themed && theme?.css) {
    const style = existingStyle ?? document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = theme.css;
    if (!existingStyle) document.head.appendChild(style);
  } else if (existingStyle) {
    existingStyle.remove();
  }

  // Product name → tab title (the un-themed title stays index.html's static one).
  if (themed && theme?.brand?.productName) {
    document.title = theme.brand.productName;
  }

  // OL-23: what the legal floor in global.css keys off. Absent on an un-themed hub, so nothing changes.
  if (themed) {
    root.dataset.hubStyled = 'true';
  } else {
    delete root.dataset.hubStyled;
  }
  applyDesignVars(themed ? theme?.brand?.design : null);
}

/** The hub favicon: session-scoped bytes into a blob: URL. Failing leaves the browser's default. */
function applyFavicon(assetId: string | undefined): () => void {
  document.getElementById(FAVICON_ID)?.remove();
  if (!assetId) return () => {};
  let current = true;
  let url: string | null = null;
  fetchAsset(assetId)
    .then(({ bytes, mediaType }) => {
      if (!current) return;
      url = URL.createObjectURL(new Blob([bytes], { type: mediaType }));
      const link = document.createElement('link');
      link.id = FAVICON_ID;
      link.rel = 'icon';
      link.href = url;
      document.head.appendChild(link);
    })
    .catch(() => {});
  return () => {
    current = false;
    if (url) URL.revokeObjectURL(url);
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<ThemeResponse | null>(null);
  const requested = useRef(false);

  const load = useCallback(() => {
    if (requested.current) return; // one fetch per session lifetime
    requested.current = true;
    getTheme()
      .then((response) => setTheme(response.themed ? response : null))
      .catch(() => setTheme(null)); // non-fatal by contract — defaults apply
  }, []);

  useEffect(() => {
    applyDomEffects(theme);
  }, [theme]);

  const faviconAssetId = theme?.themed ? theme.brand?.faviconAssetId : undefined;
  useEffect(() => applyFavicon(faviconAssetId), [faviconAssetId]);

  const design = theme?.themed ? theme.brand?.design : undefined;
  useEffect(() => loadHubFonts(design), [design]);

  return <ThemeContext.Provider value={{ theme, load }}>{children}</ThemeContext.Provider>;
}
